import { useState } from "react";
import { Send, Phone, MapPin, MessageCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AnimateIn } from "@/hooks/useAnimations";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      setStatus({ type: "success", msg: data.message });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      setStatus({ type: "error", msg: "Something went wrong. Please try again." });
    }
    setLoading(false);
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-14 sm:py-20 lg:py-24 bg-[#0A0A0A] relative grid-lines scroll-mt-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <AnimateIn>
          <div className="text-center mb-10 sm:mb-16">
            <p className="section-label text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-red-600 justify-center">Get In Touch</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight">
              Let's Build <span className="text-red-600">Something Great</span>
            </h2>
            <div className="section-divider"></div>
            <p className="text-sm text-zinc-500 mt-4 max-w-md mx-auto">Ready to take your business online? We respond to every inquiry within 24 hours.</p>
          </div>
        </AnimateIn>

        {/* Contact info row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
          <AnimateIn delay={0.18}>
            <a href="tel:+919242478118" className="flex items-center gap-4 group p-5 border border-white/5 bg-[#111111] hover:border-red-600/60 hover:-translate-y-1 hover:shadow-[4px_4px_0_0_rgba(229,9,20,0.7)] transition-all duration-300" data-testid="contact-phone-link">
              <div className="w-12 h-12 bg-red-600/10 flex items-center justify-center group-hover:bg-red-600 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shrink-0">
                <Phone size={20} className="text-red-500 group-hover:text-white transition-colors" />
              </div>
              <div>
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Phone</p>
                <p className="text-white font-bold text-sm group-hover:text-red-500 transition-colors">+91 92424 78118</p>
              </div>
            </a>
          </AnimateIn>
          <AnimateIn delay={0.36}>
            <a href="https://wa.me/919242478118" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group p-5 border border-white/5 bg-[#111111] hover:border-green-600/60 hover:-translate-y-1 hover:shadow-[4px_4px_0_0_rgba(37,211,102,0.7)] transition-all duration-300" data-testid="contact-whatsapp-link">
              <div className="w-12 h-12 bg-green-600/10 flex items-center justify-center group-hover:bg-green-600 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shrink-0">
                <MessageCircle size={20} className="text-green-500 group-hover:text-white transition-colors" />
              </div>
              <div>
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">WhatsApp</p>
                <p className="text-white font-bold text-sm group-hover:text-green-500 transition-colors">+91 92424 78118</p>
              </div>
            </a>
          </AnimateIn>
          <AnimateIn delay={0.54}>
            <a href="https://maps.google.com/?q=Bolpur+Santiniketan+Birbhum+West+Bengal+731235" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-5 border border-white/5 bg-[#111111] hover:border-red-600/60 hover:-translate-y-1 hover:shadow-[4px_4px_0_0_rgba(229,9,20,0.7)] transition-all duration-300 cursor-pointer group">
              <div className="w-12 h-12 bg-red-600/10 flex items-center justify-center group-hover:bg-red-600 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shrink-0">
                <MapPin size={20} className="text-red-500 group-hover:text-white transition-colors" />
              </div>
              <div>
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Location</p>
                <p className="text-white font-bold text-sm group-hover:text-red-500 transition-colors">Bolpur Santiniketan, Birbhum</p>
                <p className="text-[11px] text-zinc-500">West Bengal 731235, India</p>
              </div>
            </a>
          </AnimateIn>
        </div>

        {/* Full-width form */}
        <AnimateIn delay={0.72}>
          <div className="border border-white/10 bg-[#111111] p-8 sm:p-10 hover:border-red-600/20 transition-colors duration-500">
            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Name *</Label>
                  <Input id="name" data-testid="contact-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required autoComplete="name" enterKeyHint="next" placeholder="Your name" className="bg-[#0A0A0A] border border-white/10 rounded-none px-4 py-3 text-white focus:border-red-600 focus-visible:ring-0 focus-visible:ring-offset-0 transition-colors duration-300 h-12" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Email *</Label>
                  <Input id="email" type="email" inputMode="email" data-testid="contact-email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required autoComplete="email" enterKeyHint="next" placeholder="your@email.com" className="bg-[#0A0A0A] border border-white/10 rounded-none px-4 py-3 text-white focus:border-red-600 focus-visible:ring-0 focus-visible:ring-offset-0 transition-colors duration-300 h-12" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="phone" className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Phone *</Label>
                  <Input id="phone" type="tel" inputMode="tel" data-testid="contact-phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required autoComplete="tel" enterKeyHint="next" placeholder="+91 XXXXX XXXXX" className="bg-[#0A0A0A] border border-white/10 rounded-none px-4 py-3 text-white focus:border-red-600 focus-visible:ring-0 focus-visible:ring-offset-0 transition-colors duration-300 h-12" />
                </div>
                <div>
                  <Label htmlFor="subject" className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Subject *</Label>
                  <Input id="subject" data-testid="contact-subject" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} required enterKeyHint="next" placeholder="Website for my business" className="bg-[#0A0A0A] border border-white/10 rounded-none px-4 py-3 text-white focus:border-red-600 focus-visible:ring-0 focus-visible:ring-offset-0 transition-colors duration-300 h-12" />
                </div>
              </div>
              <div>
                <Label htmlFor="message" className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Message</Label>
                <Textarea id="message" data-testid="contact-message" value={form.message} onChange={e => setForm({...form, message: e.target.value})} rows={5} enterKeyHint="send" placeholder="Tell us about your project (optional)..." className="bg-[#0A0A0A] border border-white/10 rounded-none px-4 py-3 text-white focus:border-red-600 focus-visible:ring-0 focus-visible:ring-offset-0 resize-none transition-colors duration-300" />
              </div>

              {status && (
                <div data-testid="contact-status" className={`p-3 text-sm ${status.type === "success" ? "bg-green-600/10 text-green-400 border border-green-600/20" : "bg-red-600/10 text-red-400 border border-red-600/20"}`}>
                  {status.msg}
                </div>
              )}

              <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-brutal btn-shine group w-full sm:w-auto bg-red-600 text-white font-bold uppercase tracking-widest py-4 px-12 border-2 border-white hover:bg-white hover:text-black text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                {loading ? "Sending..." : <><Send size={16} className="group-hover:translate-x-1 group-hover:rotate-12 transition-transform" /> Send Message</>}
              </button>
            </form>
          </div>
        </AnimateIn>
      </div>
    </section>
  );
}
